import { faPlus, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { EUsageMetrics } from '@feathr/blackbox';
import { Button, DebouncedInput, Input, Tab } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import { SelectionActionBar } from '@feathr/extender/components';
import { InviteUsersButton } from '@feathr/extender/components/InviteUsersModal';
import { useTableSelection } from '@feathr/extender/hooks';
import { useAccount, useLocalUrl, useRole, useUsageAndQuota } from '@feathr/extender/state';
import { SELECTED_USERS, useToggle } from '@feathr/hooks';

import MFAAuthentication from './MFAAuthentication';
import CreateRoleModal from './Roles/CreateRoleModal';
import RolesTable from './Roles/RolesTable';
import BulkAssignButton from './Users/BulkAssignButton';
import usersColumns from './Users/usersColumns';
import UsersTable from './Users/UsersTable';

import * as styles from './UsersAndRolesPage.css';

function UsersAndRolesPage(): JSX.Element {
  const localUrl = useLocalUrl();
  const [search, setSearch] = useState<string | undefined>();
  const { t } = useTranslation();
  const { accountId } = useParams<{ accountId: string }>();
  const { getUsageText } = useUsageAndQuota(accountId, [EUsageMetrics.Users]);

  const { tab } = useParams<{ tab: 'roles' | 'authentication' }>();
  const isTabRoles = tab === 'roles';
  const isTabAuthentication = tab === 'authentication';
  const placeHolderText = isTabRoles ? t('Search by role...') : t('Search by name or email...');

  const [isCreateRoleModalOpen, toggleIsCreateRoleModalOpen] = useToggle(false);

  const account = useAccount();
  const { hasPermissionsLicenseOrFlag } = useRole();

  // Use new selection hook
  const userSelection = useTableSelection(SELECTED_USERS);

  // Clear the search field when switching between tabs.
  useEffect(() => {
    setSearch('');
    account.reload();
  }, [isTabRoles]);

  function handleDebouncedSearchChange(newValue?: string): void {
    setSearch(newValue ?? '');
  }

  const rawSearch = isTabRoles
    ? [{ name: { $regex: search, $options: 'i' } }]
    : [
        { first_name: { $regex: search, $options: 'i' } },
        { last_name: { $regex: search, $options: 'i' } },
        { name: { $regex: search, $options: 'i' } },
        { email: { $regex: search, $options: 'i' } },
      ];

  const filters = search
    ? {
        __raw__: {
          $or: rawSearch,
        },
      }
    : {};

  const filterElements: JSX.Element[] = [
    <DebouncedInput<string>
      defaultValue={search}
      key={'debounced-input-users-and-roles-search'}
      onChange={handleDebouncedSearchChange}
    >
      {(liveValue, onChangeLiveValue): JSX.Element => (
        <Input
          className={styles.filter}
          isClearable={true}
          key={'users-and-roles-search'}
          onChange={onChangeLiveValue}
          placeholder={placeHolderText}
          prefix={<FontAwesomeIcon icon={faSearch} />}
          type={'text'}
          value={liveValue}
        />
      )}
    </DebouncedInput>,
  ];

  const userActions: JSX.Element[] = [<InviteUsersButton key={'invite-users-button'} />];

  const roleActions: JSX.Element[] = [
    <Button
      key={'createRole'}
      name={'create-role'}
      onClick={toggleIsCreateRoleModalOpen}
      prefix={<FontAwesomeIcon icon={faPlus} />}
      type={'primary'}
    >
      {t('Create role')}
    </Button>,
  ];

  const actions: JSX.Element[] = isTabRoles ? roleActions : userActions;

  // Bulk actions for user selection
  const userBulkActions =
    !isTabRoles && userSelection.hasSelection ? (
      <BulkAssignButton userSelection={userSelection} />
    ) : undefined;

  const tabs = [
    <Tab
      exact={true}
      key={'users'}
      link={localUrl('/settings/account/users')}
      title={t('Users')}
    />,
  ];

  if (hasPermissionsLicenseOrFlag) {
    tabs.push(
      <Tab
        exact={true}
        key={'roles'}
        link={localUrl('/settings/account/users/roles')}
        title={t('Roles')}
      />,
    );
  }

  tabs.push(
    <Tab
      exact={true}
      key={'authentication'}
      link={localUrl('/settings/account/users/authentication')}
      title={t('Authentication')}
    />,
  );

  return (
    <Page
      actions={actions}
      secondaryText={!isTabRoles ? getUsageText() : undefined}
      tabs={tabs}
      title={hasPermissionsLicenseOrFlag ? t('Users & Roles') : t('Users')}
    >
      {!isTabRoles && userSelection.hasSelection && (
        <SelectionActionBar
          actions={userBulkActions}
          selectedText={
            userSelection.isSelectAllMode
              ? t('All people selected')
              : t('{{count}} person selected', { count: userSelection.count })
          }
          selection={userSelection}
        />
      )}

      {isTabAuthentication ? (
        <MFAAuthentication />
      ) : isTabRoles ? (
        <>
          {isCreateRoleModalOpen && <CreateRoleModal onClose={toggleIsCreateRoleModalOpen} />}
          <RolesTable
            filterElements={filterElements}
            // When using leftActions, the filterLabel defaults to "Filter by". We want to hide it until we can move away from it.
            filterLabel={<></>}
            filters={filters}
          />
        </>
      ) : (
        <UsersTable
          columns={usersColumns(t)}
          filterElements={filterElements}
          // When using leftActions, the filterLabel defaults to "Filter by". We want to hide it until we can move away from it.
          filterLabel={<></>}
          filters={filters}
          isSelectAllMode={userSelection.isSelectAllMode}
          onAutoSelectPage={userSelection.autoSelectPage}
          selected={userSelection.selected}
          setSelected={userSelection.setSelected}
        />
      )}
    </Page>
  );
}

export default observer(UsersAndRolesPage);
